import React from 'react';
import { Box } from '@mui/material';
import { TopBar } from './TopBar';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSafeArea } from '../hooks/useSafeArea';
import { SearchDialog } from '../components/common/SearchDialog';
import { useState } from 'react';
import { useDeepLinks } from '../hooks/useDeepLinks';

interface MainLayoutProps {
  children: React.ReactNode;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { top: safeAreaTop } = useSafeArea();
  const [searchOpen, setSearchOpen] = useState(false);
  useDeepLinks();

  const isDashboard = location.pathname === '/' || location.pathname === '/dashboard';
  const isSettings = location.pathname === '/settings';
  const isEvents = location.pathname === '/events';
  const isGeneralSettings = location.pathname === '/general-settings';
  const isRecipes = location.pathname.startsWith('/recipes');
  const isInventory = location.pathname.startsWith('/inventory');
  const isGuests = location.pathname.startsWith('/guests');
  const isWishlist = location.pathname.startsWith('/wishlist');
  const isCalendar = location.pathname === '/calendar';
  const isTasks = location.pathname.startsWith('/todo') || location.pathname.startsWith('/todos');

  // Explicitly check for pages that require the "Settings-style" TopBar (Back + Title + Search only)
  const isStandardPage = isRecipes || isInventory || isGuests || isWishlist || isTasks || isCalendar;

  const shouldShowSearch = isEvents || isStandardPage || isSettings || isGeneralSettings;

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleBackClick = () => {
    // List of root paths that should always go back to dashboard
    const rootPaths = [
      '/recipes',
      '/inventory',
      '/events',
      '/guests',
      '/settings',
      '/general-settings',
      '/todo-list',
      '/todos',
      '/hostly-club',
      '/hostly-ai',
      '/notifications',
      '/wishlist',
      '/shopping-lists',
      '/browser-history',
      '/favorite-items',
      '/calendar',
      '/hostlyclub/discover'
    ];

    if (rootPaths.some(path => location.pathname === path)) {
      navigate('/dashboard');
    } else {
      navigate(-1);
    }
  };

  // TopBar height (56px from minHeight in Toolbar) + safe area top + padding (8px top + 8px bottom from Toolbar)
  const topBarHeight = isDashboard ? 0 : 56 + safeAreaTop + 16;

  return (
    <Box sx={{
      minHeight: '100vh',
      paddingTop: `${topBarHeight}px`,
    }}>
      {!isDashboard && !isInventory && (
        <TopBar
          title={isSettings ? 'Configurações' : isEvents ? 'Calendário' : isGeneralSettings ? 'Configurações' : isRecipes ? 'Receitas' : 'Hostly'}
          showBackButton={!isDashboard}
          showSettingsButton={isGeneralSettings}
          showSearchButton={shouldShowSearch}
          showFloralButton={false}
          showMoreButton={isEvents && !isCalendar} // Only show 'More' on Events list, not Calendar or others
          onSettingsClick={isGeneralSettings ? undefined : handleSettingsClick}
          onBackClick={handleBackClick}
          onSearchClick={() => setSearchOpen(true)}
        />
      )}

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />

      <Box component="main">
        {children}
      </Box>
    </Box>
  );
};
